/**
 * PremiumNav — Apple-grade sticky top navigation.
 * v2: Dashboard + Reports moved INSIDE profile dropdown (user request).
 */
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, FolderKanban, Sparkles, GraduationCap, Youtube,
  Pen, BarChart3, Settings, User, Menu, X, Search, ChevronDown, LogOut,
  Mail, CreditCard, LayoutGrid, ListTodo, Trophy, BookOpen, Languages,
} from "lucide-react";
import { PremiumButton } from "./Primitives";
import { useUser } from "@/contexts/UserContext";

// Main top-nav (Dashboard / Reports REMOVED — they live in profile menu now)
const NAV_ITEMS = [
  { label: "Workspace",      to: "/workspace",       Icon: FolderKanban },
  { label: "AI Studio",      to: "/ai-studio",       Icon: Sparkles },
  { label: "Teaching Suite", to: "/teaching",        Icon: GraduationCap },
  { label: "YouTube Suite",  to: "/youtube-suite",   Icon: Youtube },
  { label: "Content Suite",  to: "/content-suite",   Icon: Pen },
  { label: "Indic AI",       to: "/tools/sarvam-studio", Icon: Languages, badge: "NEW" },
  { label: "All Tools",      to: "/tools",           Icon: LayoutGrid },
];

// Profile dropdown items (logged-in only)
const PROFILE_ITEMS = [
  { label: "My Dashboard", to: "/dashboard",       Icon: LayoutDashboard, desc: "Overview & stats" },
  { label: "Creator Earnings", to: "/creator/incentive", Icon: Trophy,   desc: "₹150/video · ₹50/short" },
  { label: "YouTube CRM",  to: "/studio",          Icon: Youtube,         desc: "Studio & channels" },
  { label: "My Tasks",     to: "/studio/my-tasks", Icon: ListTodo,        desc: "Task manager" },
  { label: "Reports",      to: "/reports",         Icon: BarChart3,       desc: "Saved reports" },
  { label: "Blog",         to: "/blogs",           Icon: BookOpen,        desc: "Guides & insights" },
  { label: "Profile",      to: "/profile",         Icon: User,            desc: "Account details" },
  { label: "Billing",      to: "/pricing",         Icon: CreditCard,      desc: "Plan & billing" },
  { label: "Settings",     to: "/settings",        Icon: Settings,        desc: "Preferences" },
];

export default function PremiumNav({ logoSrc, variant }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn, logout } = useUser();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const userName = user?.full_name || user?.name || user?.email?.split("@")[0] || "";
  const userEmail = user?.email || "";
  const userPlan = user?.plan || "free";

  const isDark = variant === "dark";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setProfileOpen(false); }, [pathname]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [profileOpen]);

  // Theme tokens
  const bg = isDark
    ? (scrolled ? "bg-[#0b0f1a]/90 backdrop-blur-xl border-b border-white/10 shadow-soft" : "bg-[#0b0f1a]/70 backdrop-blur-md border-b border-transparent")
    : (scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-soft" : "bg-white/60 backdrop-blur-md border-b border-transparent");
  const logoColor = isDark ? "text-white" : "text-[#111111]";
  const linkInactive = isDark ? "text-white/65 hover:text-white" : "text-slate-600 hover:text-[#111111]";
  const linkActiveBg = isDark ? "bg-white/10" : "bg-brand-50";
  const searchBtnText = isDark ? "text-white/55 hover:text-white hover:bg-white/10" : "text-slate-500 hover:text-[#111111] hover:bg-slate-100";
  const profileText = isDark ? "text-white hover:bg-white/10" : "text-[#111111] hover:bg-slate-100";
  const loginText = isDark ? "text-white hover:text-[#A855F7]" : "text-[#111111] hover:text-[#A855F7]";
  const kbdCls = isDark ? "bg-white/10 border-white/15 text-white/55" : "bg-slate-100 border-slate-200 text-slate-500";

  return (
    <header
      data-testid="premium-nav"
      className={`sticky top-0 z-50 transition-all duration-300 ${bg}`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="premium-nav-logo">
            {logoSrc ? (
              <img src={logoSrc} alt="MarkeeMix" className="h-7 w-auto"/>
            ) : (
              <span className={`font-heading text-lg font-bold tracking-tight ${logoColor}`}>
                Markee<span className="text-[#A855F7]">Mix</span>
              </span>
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="premium-nav-links">
            {NAV_ITEMS.map((item) => {
              const active = pathname === item.to || pathname.startsWith(item.to + "/");
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.replace("/", "")}`}
                  className={`relative px-3.5 py-2 text-sm font-medium font-sans rounded-lg transition-colors ${
                    active ? "text-[#A855F7]" : linkInactive
                  }`}
                >
                  {item.label}
                  {item.badge && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded uppercase tracking-wider">{item.badge}</span>
                  )}
                  {active && (
                    <motion.span
                      layoutId="nav-active-pill"
                      className={`absolute inset-0 ${linkActiveBg} rounded-lg -z-10`}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            <button
              className={`hidden md:flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors font-sans ${searchBtnText}`}
              onClick={() => window.dispatchEvent(new CustomEvent("mm:open-command-palette"))}
              data-testid="premium-nav-search"
            >
              <Search className="w-4 h-4"/>
              <span>Search</span>
              <kbd className={`hidden xl:inline-block px-1.5 py-0.5 text-[10px] border rounded font-mono ${kbdCls}`}>⌘K</kbd>
            </button>

            {isLoggedIn ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen((v) => !v)}
                  data-testid="premium-nav-profile"
                  className={`flex items-center gap-2 px-2 py-1.5 text-sm font-medium font-sans rounded-lg transition-colors ${profileText}`}
                >
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white text-xs font-bold">
                    {userName ? userName[0].toUpperCase() : <User className="w-3.5 h-3.5"/>}
                  </span>
                  <span className="hidden xl:inline max-w-[100px] truncate">{userName || "Account"}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${profileOpen ? "rotate-180" : ""} ${isDark ? "text-white/40" : "text-slate-400"}`}/>
                </button>

                <AnimatePresence>
                  {profileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-72 rounded-2xl border border-slate-200 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.08)] overflow-hidden"
                      data-testid="premium-profile-dropdown"
                    >
                      {/* User info header */}
                      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                        <div className="flex items-center gap-3">
                          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white text-sm font-bold shrink-0">
                            {userName ? userName[0].toUpperCase() : <User className="w-4 h-4"/>}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-[#111111] truncate">{userName || "Account"}</p>
                            {userEmail && (
                              <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                                <Mail className="w-3 h-3"/> {userEmail}
                              </p>
                            )}
                          </div>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                            userPlan === "free" ? "bg-slate-200 text-slate-700" : "bg-[#A855F7] text-white"
                          }`}>
                            {userPlan}
                          </span>
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="py-1.5">
                        {PROFILE_ITEMS.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setProfileOpen(false)}
                            data-testid={`profile-menu-${item.to.replace("/", "")}`}
                            className="flex items-start gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors group"
                          >
                            <item.Icon className="w-4 h-4 text-slate-500 mt-0.5 group-hover:text-[#A855F7] transition-colors"/>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#111111] leading-tight">{item.label}</p>
                              {item.desc && <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>}
                            </div>
                          </Link>
                        ))}
                      </div>

                      <div className="border-t border-slate-200 py-1.5">
                        <button
                          onClick={() => { setProfileOpen(false); logout?.(); navigate("/"); }}
                          data-testid="profile-menu-logout"
                          className="flex items-center gap-3 w-full px-4 py-2.5 hover:bg-red-50 text-red-600 transition-colors"
                        >
                          <LogOut className="w-4 h-4"/>
                          <span className="text-sm font-medium">Log out</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  data-testid="premium-nav-login"
                  className={`hidden sm:inline-flex px-3.5 py-1.5 text-sm font-semibold font-sans transition-colors ${loginText}`}
                >
                  Log in
                </button>
                <PremiumButton size="sm" onClick={() => navigate("/signup")} data-testid="premium-nav-cta">
                  Start free
                </PremiumButton>
              </>
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className={`lg:hidden p-2 rounded-lg ${isDark ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"}`}
              data-testid="premium-nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18 }}
            className="lg:hidden border-t border-slate-200 bg-white"
            data-testid="premium-nav-mobile-menu"
          >
            <nav className="max-w-7xl mx-auto px-5 py-3 grid grid-cols-1 gap-1">
              {NAV_ITEMS.map((item) => {
                const active = pathname === item.to || pathname.startsWith(item.to + "/");
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-sans ${
                      active ? "bg-brand-50 text-[#A855F7]" : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <item.Icon className="w-4 h-4"/>
                    {item.label}
                  </Link>
                );
              })}
              {isLoggedIn && (
                <>
                  <div className="h-px bg-slate-200 my-2"/>
                  <p className="px-3 text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-1">Account</p>
                  {PROFILE_ITEMS.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-sans text-slate-700 hover:bg-slate-50"
                    >
                      <item.Icon className="w-4 h-4"/> {item.label}
                    </Link>
                  ))}
                  <button
                    onClick={() => { logout?.(); navigate("/"); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium font-sans text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4"/> Log out
                  </button>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
